/**
 * list_organization_members Tool - List members of an organization
 *
 * Helps AI agents discover who is in an organization and their roles.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';

export interface ListOrganizationMembersArgs {
  organizationId: string;
}

export const listOrganizationMembersToolDefinition: Tool = {
  name: 'list_organization_members',
  description: `List all members of an organization, including their roles and status.

Returns active and suspended members with email, role (admin/member), owner status, and join date.

Use this tool to:
- Check who has access to an organization
- Verify a user's role before inviting or managing members
- See suspended members (removed due to plan downgrade)

Typical workflow:
1. list_organizations → Find the organization
2. list_organization_members(organizationId: "...") → See who's in it
3. invite_organization_member(organizationId: "...", email: "...", role: "member") → Add someone`,
  inputSchema: {
    type: 'object',
    properties: {
      organizationId: {
        type: 'string',
        description: 'The organization ID to list members for',
      },
    },
    required: ['organizationId'],
  },
};

export async function handleListOrganizationMembers(
  args: ListOrganizationMembersArgs,
  jobClient: JobClient
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const data = await jobClient.listOrganizationMembers(args.organizationId);

    if (data.items.length === 0) {
      return {
        content: [{
          type: 'text',
          text: 'No members found in this organization.',
        }],
      };
    }

    const memberList = data.items.map(member => {
      const name = [member.firstName, member.lastName].filter(Boolean).join(' ');
      const displayName = name || member.email;
      const ownerTag = member.isOwner ? ' (Owner)' : '';
      const statusTag = member.status === 'suspended' ? ' [SUSPENDED]' : '';
      return `- ${displayName}${ownerTag}${statusTag}\n  Email: ${member.email}\n  Role: ${member.role}\n  Joined: ${member.joinedAt}`;
    }).join('\n\n');

    const limitInfo = data.memberLimit > 0
      ? `\nMember limit: ${data.activeMemberCount}/${data.memberLimit}`
      : '';

    return {
      content: [{
        type: 'text',
        text: `${data.total} member(s) (${data.activeMemberCount} active, ${data.suspendedMemberCount} suspended):${limitInfo}

${memberList}

Next steps:
- Invite a member: invite_organization_member(organizationId: "${args.organizationId}", email: "user@example.com", role: "member")`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };

    return {
      content: [{
        type: 'text',
        text: `Failed to list organization members

Error: ${err.message}
${err.status ? `Status: ${err.status}` : ''}

Make sure you have access to this organization and your PAT is valid.`,
      }],
      isError: true,
    };
  }
}
